// =============================================================================
// io/sync/saveTrade.ts — the trade form's write.
// -----------------------------------------------------------------------------
// The widest write in this client: thirty-one fields across six tabs. It follows
// `saveAnnotation`'s shape exactly, and the reason is worth restating rather than
// referring to, because the wider the patch the more tempting the shortcut is.
//
//   1. `loadFullTrades([id], ledger)` → the row with all 48 columns, and the
//      ledger entry that is what makes a save legal at all (D46)
//   2. apply the form's OWNED fields to THAT object
//   3. `tradeToDb` the result and UPDATE by id
//
// ── 🚨 WHY THIS IS A ROW WRITE WHERE BULK EDIT IS A COLUMN WRITE (D70) ──
//
// Bulk edit sends `update({ grade: 'A' })` — named columns, nothing else — because
// it never reads the rows in full and must not blank the 25 columns it lacks.
// This form is the opposite case: it HAS the whole row, because it refuses to
// render a field until the hydration read lands. So `tradeToDb` writing all 48
// columns is correct here and unsafe there. Same table, two write shapes, and the
// difference is entirely "did we read it first".
//
// ── 🚨 WHAT THE FORM DOES **NOT** OWN, AND WHY THAT IS THE SAFETY PROPERTY ──
//
// `OWNED_FIELDS` below is the whole list. Everything absent from it survives the
// save untouched because it is still sitting on the hydrated object:
//
//   mood                 — the old client removed the input and kept the value
//   criteriaMet          — the entry-criteria checklist is NOT ported (it resolves
//                          against pattern rule VERSIONS, a subsystem this client
//                          does not load). Rendering an empty checklist and saving
//                          it would clear marks the user never saw.
//   maePoints, mfePoints — logger facts. Read-only in the old client too.
//   maeSecs, mfeSecs
//   contractFull         — the exact month traded; set at import, never by hand
//   executions           — the per-fill breakdown, rides `extras`
//   imported, flagged, flagReasons, sourceAccount, syntheticClose,
//   controllableLossAmount
//
// ⚠ The list is the CONTRACT. A field added to `TradeFormValues` without a line
// here is silently not saved; a field added here without a control on screen is a
// blank written over real data (D61). Both directions have a test.
//
// ── 🚨 SAVING MARKS THE TRADE REVIEWED ──
//
// `commitTrade` ends with `t.annotated = true`, unconditionally, and this port
// keeps it. That is a real consequence — the trade leaves Needs Review and its
// unchecked discipline flags start counting as "this held" — so the form SAYS SO
// beside the Save button rather than leaving it to be discovered.
// =============================================================================

import type { SupabaseClient } from '@supabase/supabase-js';
import type { Trade } from '../../domain/types';
import { sessionDateFor, sessionFromTime } from '../../domain/session';
import { loadFullTrades } from './load';
import { tradeToDb } from './serialize';
import type { HydrationLedger } from './hydration';
import { reportFailure, reportFailureAs } from '../diagnostics/errorLog';

/**
 * The fields the form writes. Every one has a control on one of the six tabs.
 *
 * ⚠ Listed as data rather than as assignments so a test can assert BOTH
 * directions against the form's own value type.
 */
export const OWNED_FIELDS = [
  // Execution
  'accountId', 'date', 'sessionDate', 'contract', 'side', 'qty',
  'timeIn', 'timeOut', 'entry', 'exit', 'fees', 'feesAuto',
  'points', 'grossPnl', 'netPnl',
  // Risk
  'riskPoints', 'rrPlanned', 'rrRealized', 'plannedStop', 'plannedTarget', 'riskAtEntry',
  // Setup
  'patternId', 'exitModelId', 'grade', 'sessionLabel', 'marketRegime', 'newsEvent',
  'confluences',
  // Process
  'booleans', 'mistakeTags', 'tags',
  // Notes & media
  //
  // ⚠ `screenshots` and `recordings` JOINED THIS LIST when the Notes tab gained
  // a real gallery, and the move is only legal because of the hydration read at
  // step 1. D62's rule is not "read it" — it is "render what you read and write
  // back what you rendered". Both arrays are read whole, every element gets a
  // tile or a player, and the user's add/remove IS the new list. Before the
  // gallery existed, owning them would have written `[]` over real attachments
  // from a form that displayed a count and nothing else.
  'notes', 'screenshots', 'recordings',
  // Set by the act of saving
  'annotated',
] as const;

export type OwnedField = (typeof OWNED_FIELDS)[number];

/**
 * Exactly what the form hands over — already derived, already validated.
 *
 * ⚠ `rrPlanned` is the canonical RATIO, never the R:R input's raw value. See
 * `storedRrPlanned` in `domain/tradeForm.ts`: under the `pts` unit the input holds
 * a distance, and storing that as a ratio is a silent 3× error on a 3-point stop.
 */
export type TradePatch = Pick<Trade, OwnedField>;

export interface SaveTradeResult {
  trade: Trade | null;
  error: string | null;
}

/**
 * Fields that ride `extras` and that the form does set.
 *
 * ⚠ Separate from `TradePatch` because they are not `Trade` fields — `extras` is
 * the forward-compat seam and typing these into the domain model would claim a
 * column that does not exist.
 */
export interface TradeExtrasPatch {
  /**
   * Whether the session label is still auto-derived.
   *
   * The old client's rule: a pick that DIFFERS from the value derived from
   * `timeIn` is a manual override and locks; "—" or a pick matching the derived
   * value stays auto and re-derives when the session timezone changes.
   */
  sessionAuto: boolean;
  /**
   * The pinned pattern rule-version, or `null` to clear it.
   *
   * ⚠ `null` DELETES the key rather than storing a null. `resolveCriteria` reads
   * `t.patternVersion != null` to decide between the pin and the pattern's live
   * rules, so a stored null would be truthy-ish in the wrong place.
   */
  patternVersion: number | null;
}

/**
 * Apply the patch to a hydrated trade.
 *
 * Exported so a test can exercise the merge without a database — and so the
 * "every owned field is assigned" check has something to run against.
 */
export function applyTradePatch(
  full: Trade, patch: TradePatch, extras: TradeExtrasPatch,
): Trade {
  const next = { ...full } as unknown as Record<string, unknown>;
  for (const k of OWNED_FIELDS) next[k] = (patch as unknown as Record<string, unknown>)[k];

  next['sessionAuto'] = extras.sessionAuto;
  if (extras.patternVersion == null) delete next['patternVersion'];
  else next['patternVersion'] = extras.patternVersion;

  return next as unknown as Trade;
}

/**
 * Recompute the session-day from the (possibly edited) date and entry time.
 *
 * ⚠ Ported because the old client does it on every save: an evening trade whose
 * date was corrected must move to the right session day, and `session_date` is
 * what every session-mode surface groups by. Leaving the stored value alone would
 * strand the trade on the day it was first imported under.
 */
export function recomputeSessionDate(date: string, timeIn: string, tz: string): string | null {
  if (!date) return null;
  return sessionDateFor(date, timeIn || '', tz) as unknown as string;
}

/**
 * The session label and its auto flag, from the picked value and the clock.
 *
 * ⚠ Returns BOTH, because they are one decision. Computing the label here and the
 * flag at the call site is how they come to disagree — and a label that says
 * "NY-AM" with `sessionAuto: true` re-derives to something else on the next
 * timezone change, silently.
 */
export function resolveSessionLabel(
  picked: string, date: string, timeIn: string, tz: string,
): { sessionLabel: string; sessionAuto: boolean } {
  const auto = sessionFromTime(timeIn || null, date, tz);
  if (picked && picked !== auto) return { sessionLabel: picked, sessionAuto: false };
  return { sessionLabel: picked || auto, sessionAuto: true };
}

/**
 * The pattern-version pin, ported from `setPatternTag`.
 *
 * ⚠ An UNCHANGED pattern keeps its existing pin — that is the whole point of a
 * pin, and re-stamping it on every save would silently re-judge an old trade
 * against rules written after it.
 */
export function resolvePatternVersion(
  oldPatternId: string | null,
  newPatternId: string,
  existingPin: number | null,
  liveVersionOfNew: number | null,
): number | null {
  if (!newPatternId) return null;                                   // cleared → no pin
  if (newPatternId === oldPatternId && existingPin != null) return existingPin;
  return liveVersionOfNew ?? 1;
}

// ---------------------------------------------------------------------------
// The writes
// ---------------------------------------------------------------------------

/**
 * Save an EXISTING trade.
 *
 * ⚠ UPDATE by id, never upsert — `saveAnnotation`'s reasoning: an upsert on a
 * mistyped id inserts a phantom trade carrying 48 columns of annotation and no
 * executions behind it, whereas an update matching nothing is detectable.
 */
export async function saveTrade(
  sb: SupabaseClient,
  tradeId: string,
  patch: TradePatch,
  extras: TradeExtrasPatch,
  userId: string,
  ledger: HydrationLedger,
): Promise<SaveTradeResult> {
  let full: Trade | undefined;
  try {
    const rows = await loadFullTrades(sb, [tradeId], ledger);
    full = rows[0];
  } catch (e) {
    return {
      trade: null,
      error: `Couldn't load the trade in full, so nothing was saved: `
        + `${reportFailure('save trade', e)}`,
    };
  }

  if (!full) {
    return {
      trade: null,
      error: 'That trade no longer exists — it may have been deleted in another tab. '
        + 'Nothing was saved.',
    };
  }

  const next = applyTradePatch(full, patch, extras);

  let row: Record<string, unknown>;
  try {
    row = tradeToDb(next, userId, ledger) as unknown as Record<string, unknown>;
  } catch (e) {
    return { trade: null, error: `Refusing to save: ${reportFailure('save trade', e)}` };
  }

  const { data, error } = await sb.from('trades').update(row).eq('id', tradeId).select('id');
  if (error) {
    return {
      trade: null,
      error: reportFailureAs('save trade', error, `Save failed: ${error.message}`),
    };
  }
  if (!Array.isArray(data) || data.length === 0) {
    return {
      trade: null,
      error: 'The save matched no rows, so nothing changed. The trade may have been '
        + 'deleted, or it belongs to another user.',
    };
  }
  return { trade: next, error: null };
}

/**
 * Insert a NEW trade.
 *
 * ⚠ `markNew`, not `markHydrated`. The distinction is the ledger's whole point:
 * this object was CONSTRUCTED here, complete, so there is nothing to have failed
 * to read — whereas `markHydrated` claims a read happened. Import makes the same
 * call for the same reason.
 *
 * ⚠ INSERT, never upsert. An upsert would silently overwrite a real trade if the
 * minted id ever collided, and a collision is exactly the case where a loud
 * duplicate-key error is what you want.
 */
export async function insertTrade(
  sb: SupabaseClient,
  blank: Trade,
  patch: TradePatch,
  extras: TradeExtrasPatch,
  userId: string,
  ledger: HydrationLedger,
): Promise<SaveTradeResult> {
  const id = String(blank.id);
  if (!id) return { trade: null, error: 'Refusing to insert a trade with no id.' };
  ledger.markNew(id);

  const next = applyTradePatch(blank, patch, extras);

  let row: Record<string, unknown>;
  try {
    row = tradeToDb(next, userId, ledger) as unknown as Record<string, unknown>;
  } catch (e) {
    // ⚠ Scoped `add trade`, not `save trade`. The two paths fail for different
    // reasons — a rejected INSERT is a trade that never existed, an UPDATE is one
    // the user may think is still on screen — and the log line is where they part.
    return { trade: null, error: `Refusing to save: ${reportFailure('add trade', e)}` };
  }

  const { error } = await sb.from('trades').insert(row);
  // ⚠ `add trade` again, for the same reason the throw above is scoped that way:
  // a rejected INSERT and a rejected UPDATE are different failures in the log.
  if (error) {
    return {
      trade: null,
      error: reportFailureAs('add trade', error, `Save failed: ${error.message}`),
    };
  }
  return { trade: next, error: null };
}

/**
 * Delete one trade.
 *
 * ⚠ HARD delete — `trades` has no soft-delete column, unlike accounts with their
 * 30-day grace. The caller confirms; this just does it and reports whether a row
 * actually matched, because a delete that quietly matched nothing looks identical
 * to a successful one.
 */
export async function deleteTrade(
  sb: SupabaseClient, userId: string, tradeId: string,
): Promise<{ error: string | null }> {
  const { data, error } = await sb
    .from('trades').delete().eq('user_id', userId).eq('id', tradeId).select('id');
  if (error) {
    return { error: reportFailureAs('delete trade', error, `Delete failed: ${error.message}`) };
  }
  if (!Array.isArray(data) || data.length === 0) {
    return { error: 'The delete matched no rows — the trade may already be gone.' };
  }
  return { error: null };
}
